import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { waitSeconds } from '@reflact/tsutil';
import { InactiveBotServiceService } from '@shared-angular/services/inactive-bot-service.service';
import { UsertokenService } from '@shared-angular/services/usertoken.service';
import { ClientToServerEvents, HistoryItemResponse, ServerToClientEvents, SessionState, WsClient } from '@shared/index';
import { Subject } from 'rxjs';

export type MySocket = WsClient<ServerToClientEvents, ClientToServerEvents>;

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private ioSocket?: MySocket;
  public disconnectReason?: keyof ServerToClientEvents;
  public onAddUserMessage: Subject<{ message: HistoryItemResponse }> = new Subject<{ message: HistoryItemResponse }>();
  public onRunUpdate: Subject<Omit<HistoryItemResponse, "content"> & {
    snapshot: string;
    deltaStr: string;
  }> = new Subject<Omit<HistoryItemResponse, "content"> & {
    snapshot: string;
    deltaStr: string;
  }>();
  public onStateUpdate: Subject<{ sessionState: SessionState }> = new Subject<{ sessionState: SessionState }>();
  public onRunComplete: Subject<Parameters<ServerToClientEvents["runComplete"]>[0]> = new Subject<Parameters<ServerToClientEvents["runComplete"]>[0]>();
  public onMessageDeletion: Subject<Parameters<ServerToClientEvents["deleteMessages"]>[0]> = new Subject<Parameters<ServerToClientEvents["deleteMessages"]>[0]>();
  public onCreateMessagesInThread: Subject<Parameters<ServerToClientEvents["createMessagesInThread"]>[0]> = new Subject<Parameters<ServerToClientEvents["createMessagesInThread"]>[0]>();
  public onFeedbackUpdate: Subject<Parameters<ServerToClientEvents["updateFeedback"]>[0]> = new Subject<Parameters<ServerToClientEvents["updateFeedback"]>[0]>();
  public onFeedbackDeletion: Subject<Parameters<ServerToClientEvents["deleteFeedback"]>[0]> = new Subject<Parameters<ServerToClientEvents["deleteFeedback"]>[0]>();
  public onSupervisorMessage: Subject<Parameters<ServerToClientEvents["supervisorMessage"]>[0]> = new Subject<Parameters<ServerToClientEvents["supervisorMessage"]>[0]>();
  public onLogoutOldSession: Subject<Parameters<ServerToClientEvents["logoutoldsession"]>[0]> = new Subject<Parameters<ServerToClientEvents["logoutoldsession"]>[0]>();
  public onRunFailed: Subject<Parameters<ServerToClientEvents["runFailed"]>[0]> = new Subject<Parameters<ServerToClientEvents["runFailed"]>[0]>();
  public onRunIncomplete: Subject<Parameters<ServerToClientEvents["runIncomplete"]>[0]> = new Subject<Parameters<ServerToClientEvents["runIncomplete"]>[0]>();
  public onDisconnect: Subject<undefined> = new Subject<undefined>();
  public onReflactTrainAuthInvalid: Subject<Parameters<ServerToClientEvents["reflactTrainAuthInvalid"]>[0]> = new Subject<Parameters<ServerToClientEvents["reflactTrainAuthInvalid"]>[0]>();

  private prevState: SessionState | "" = "";
  constructor(
    public route: ActivatedRoute,
    public inActiveService: InactiveBotServiceService,
    private usertokenService: UsertokenService
  ) { }

  public async getIo(): Promise<MySocket> {

    if (this.ioSocket) {
      return this.ioSocket;
    }
    await waitSeconds(1);
    return this.getIo();
  }

  public connectToThread(botid: string) {
    if (this.ioSocket != null) {
      return;
    }

    this.ioSocket = new WsClient<ServerToClientEvents, ClientToServerEvents>({
      autoConnect: true,
      path: '/api/ws',
      extraHeaders: {
        Ut: "" + this.usertokenService.getUserToken(),
        botid
      },
      // Re-read the token on every reconnect (timer-, visibility- or
      // online-triggered) instead of reusing the snapshot taken above —
      // without this, a socket that outlives the token's lifetime (1 day)
      // reconnects forever with an already-expired token.
      refreshAuthParams: () => ({ Ut: "" + this.usertokenService.getUserToken(), botid })
    });

    this.ioSocket.on('connect', () => { });

    this.ioSocket.on('disconnect', () => {
      this.onDisconnect.next(undefined);
    });

    this.ioSocket.on('thereCanOnlyBeOne', () => {
      this.disconnectReason = 'thereCanOnlyBeOne';
      this.ioSocket?.disconnect();
    });

    // Wird emitted, wenn eine neue Message von User geschickt wurde
    this.ioSocket.on("addUserMessage", (data) => {
      this.inActiveService.botWasActive();
      this.onAddUserMessage.next(data);
    });

    // Wird emitted, wenn der Server einen neuen Teil der Antwort bekommen hat
    this.ioSocket.on("runUpdate", (data) => {
      this.inActiveService.botWasActive();
      this.onRunUpdate.next(data);
    });

    this.ioSocket.onAnyOutgoing((eventName: string, ...args) => {
      // triggered when the event is sent
      window.RAGAI.EVENTS.triggerEventHook("io_out_" + eventName, args);
    });

    this.ioSocket.onAny((eventName: string, ...args) => {
      window.RAGAI.EVENTS.triggerEventHook("io_in_" + eventName, args);
      // not triggered when the acknowledgement is received
    });


    this.ioSocket.on("runComplete", (data) => {
      this.onRunComplete.next(data);
    });
    // Wird emitted, wenn der Server den State des Bots aktualisiert
    this.ioSocket.on("stateUpdate", (data) => {

      if (this.prevState === data.sessionState) {
        return;
      }
      this.prevState = data.sessionState;
      this.onStateUpdate.next(data);
      if (data.sessionState == "idle") {
        window.RAGAI.WAITFOR._initialIdleReached();
        window.RAGAI.WAITFOR._nextIdleReached();
      }

    });

    // Wird emitted, wenn der Server einen Custom ToolKit ausführt
    this.ioSocket.on("customToolKitExec", (data) => {
      switch (data.type) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        case 'js':
          void this.executeJsFromServer(data.content);
          break;
      }
    });
    this.ioSocket.on("supervisorExec", async (data, cb) => {
      const result = await this.executeSupervisorJsFromServer(data.js);
      cb(result);
    });

    this.ioSocket.on("createMessagesInThread", (data) => {
      this.onCreateMessagesInThread.next(data);
    });

    this.ioSocket.on("deleteMessages", (data) => {
      this.onMessageDeletion.next(data);
    });

    this.ioSocket.on("updateFeedback", (data) => {
      this.onFeedbackUpdate.next(data);
    });

    this.ioSocket.on("deleteFeedback", (data) => {
      this.onFeedbackDeletion.next(data);
    });

    this.ioSocket.on("supervisorMessage", (data) => {
      this.onSupervisorMessage.next(data);
    });

    this.ioSocket.on("logoutoldsession", (data) => {
      this.onLogoutOldSession.next(data);
    });

    this.ioSocket.on("runFailed", (message) => {
      this.onRunFailed.next(message);
    });

    this.ioSocket.on("runIncomplete", (message) => {
      this.onRunIncomplete.next(message);
    });

    this.ioSocket.on("reflactTrainAuthInvalid", (data) => {
      this.onReflactTrainAuthInvalid.next(data);
    });
  }

  private async executeSupervisorJsFromServer(jsStr: string): Promise<unknown> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const r = await eval(jsStr)() as unknown;
      return r;
    } catch (e) {
      console.error("Error on external javascript execution for:  " + jsStr, e);
      return null;
    }
  }

  private async executeJsFromServer(jsStr: string): Promise<unknown> {
    try {
      const r = eval(jsStr) as Promise<unknown>;
      const result = await r;
      return result;
    } catch (e) {
      console.error("Error on external javascript execution for:  " + jsStr, e);
      return null;
    }
  }
}
